// src/pages/ApprovazioneNoteSpesePage.js
import React, { useState, useEffect } from "react";
import { collection, query, where, onSnapshot, doc, updateDoc, serverTimestamp, getDocs, addDoc, deleteDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { messaggioErrore } from "../utils/erroriUtente";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { Receipt, CheckSquare, X, RotateCcw } from "lucide-react";
import { useToast } from "../utils/Toast";
import { useCanSee } from "../hooks/useCanSee";
import { numIt } from "../utils/economiaCommessa";

const MESI = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno",
               "Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];

const STATI = {
  inviata:   { label:"In attesa", bg:"#dbeafe", col:"#1d4ed8" },
  approvata: { label:"Approvata", bg:"#d1fae5", col:"#065f46" },
  respinta:  { label:"Respinta",  bg:"#fee2e2", col:"#991b1b" },
};

// Importi della nota spese: stessa lettura di tutta l'app ("1.234,50" → 1234,5)
function parseN(v) { return numIt(v); }

export default function ApprovazioneNoteSpesePage() {
  const { currentUser, isOrgAdmin, isPlatformOwner } = useAuth();
  const { effectiveOrgId: orgId } = useOrg();
  const { addToast } = useToast();

  const [noteSpese, setNoteSpese] = useState([]);
  const [utenti, setUtenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [elaborandoId, setElaborandoId] = useState(null);
  const [filtroStato, setFiltroStato] = useState("inviata");

  const canSee = useCanSee();
  const canManage = isOrgAdmin || isPlatformOwner || canSee("approvazione-note-spese");

  // Carica utenti
  useEffect(() => {
    if (!orgId) return;
    return onSnapshot(
      query(collection(db,"users"), where("organizationId","==",orgId), where("enabled","==",true)),
      snap => setUtenti(snap.docs.map(d => d.data()))
    );
  }, [orgId]);

  // Carica note spese inviata/approvata/respinta
  useEffect(() => {
    if (!orgId) return;
    setLoading(true);
    const q = query(collection(db,"noteSpese"),
      where("orgId","==",orgId),
      where("stato","in",["inviata","approvata","respinta"])
    );
    return onSnapshot(q, snap => {
      setNoteSpese(snap.docs.map(d => ({ id:d.id, ...d.data() })));
      setLoading(false);
    });
  }, [orgId]);

  const nomeUtente = (uid) => {
    const u = utenti.find(u => u.uid === uid);
    return u?.displayName || uid?.slice(0,8)+"…";
  };

  const totaleNota = (righe={}) => {
    return Object.values(righe).reduce((tot, r) => {
      return tot + parseN(r.rimborsoKm) + parseN(r.pedaggi) + parseN(r.parcheggi) + parseN(r.barRistoranti) + parseN(r.varie);
    }, 0);
  };

  const aggiornaStato = async (nota, nuovoStato) => {
    // Separazione dei compiti: nessuno può approvare o respingere una propria nota spese,
    // titolare o admin che sia — eccetto il Platform Owner, per poter fare le sue prove.
    // Quando lo fa, viene sempre avvertito chiaramente che sta usando un privilegio che un
    // utente normale non avrebbe — mai un'azione silenziosa.
    const staBypassando = (nuovoStato === "approvata" || nuovoStato === "respinta") && nota.uid === currentUser?.uid;
    if (staBypassando && !isPlatformOwner) {
      addToast("Non puoi approvare o respingere una tua stessa nota spese — serve un altro amministratore","error");
      return;
    }
    // Doppio clic (o una rete lenta) altrimenti potrebbe creare due movimenti economici
    // identici per la stessa nota — qui usiamo addDoc, che genera sempre un nuovo id, a
    // differenza dell'id deterministico già usato per le Presenze.
    if (elaborandoId === nota.id) return;
    setElaborandoId(nota.id);
    try {
    if (nuovoStato === "approvata") {
        const righe = nota.righe || {};
        const righeScartate = [];
        for (const [giornoStr, r] of Object.entries(righe)) {
          const totaleRiga = parseN(r.rimborsoKm) + parseN(r.pedaggi) + parseN(r.parcheggi) + parseN(r.barRistoranti) + parseN(r.varie);
          if (totaleRiga <= 0) continue;
          if (!r.commessaId) { righeScartate.push(giornoStr); continue; }
          // Id fisso per nota e giorno: approvare due volte (doppio clic, due amministratori
          // insieme, o la "rete di sicurezza" della pagina Nota spese) riscrive lo stesso
          // movimento invece di crearne un secondo.
          await setDoc(doc(db,"movimentiEconomici",`ns_${nota.id}_${giornoStr}`), {
            projectId: r.commessaId || "",
            organizationId: orgId,
            tipo: "costo",
            categoriaId: "nota_spese",
            categoriaLabel: "Note Spese",
            importo: totaleRiga,
            dati: { descrizione: `Nota spese — ${nomeUtente(nota.uid)} — ${giornoStr}/${nota.mese}/${nota.anno}`, dipendente: nomeUtente(nota.uid) },
            notaSpeseRef: nota.id,
            createdAt: serverTimestamp(),
          });
        }
        if (righeScartate.length>0) {
          addToast(`Approvata, ma ${righeScartate.length} riga/e senza commessa collegata non sono state contabilizzate (giorni: ${righeScartate.join(", ")})`,"info");
        }
      }

      // Elimina movimenti se respinta o rimessa in bozza
      if (nuovoStato === "respinta" || nuovoStato === "bozza") {
        const movSnap = await getDocs(query(collection(db,"movimentiEconomici"), where("notaSpeseRef","==",nota.id), where("organizationId","==",orgId)));
        for (const d of movSnap.docs) await deleteDoc(d.ref);
      }

      await updateDoc(doc(db,"noteSpese",nota.id), {
        stato: nuovoStato,
        [`${nuovoStato}Da`]: currentUser.uid,
        [`${nuovoStato}At`]: serverTimestamp(),
        ...(staBypassando ? { [`${nuovoStato}ComeBypassPlatformOwner`]: true } : {}),
      });

      const label = staBypassando
        ? `⚠️ ${nuovoStato==="approvata"?"Approvata":"Respinta"} come Platform Owner — un utente normale non potrebbe farlo su una propria nota`
        : nuovoStato==="approvata" ? "Nota spese approvata ✅"
        : nuovoStato==="respinta" ? "Nota spese respinta"
        : "Nota spese rimessa in compilazione";
      addToast(label, staBypassando ? "info" : nuovoStato==="approvata" ? "success" : "info");
    } catch(e) {
      addToast(messaggioErrore(e), "error");
    } finally {
      setElaborandoId(null);
    }
  };

  const noteFiltrate = noteSpese
    .filter(n => filtroStato === "tutti" ? true : n.stato === filtroStato)
    .sort((a,b) => (b.updatedAt?.toMillis?.()||0) - (a.updatedAt?.toMillis?.()||0));

  const count = {
    inviata:   noteSpese.filter(n=>n.stato==="inviata").length,
    approvata: noteSpese.filter(n=>n.stato==="approvata").length,
    respinta:  noteSpese.filter(n=>n.stato==="respinta").length,
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", overflow:"hidden" }}>
      {/* Header */}
      <div style={{ background:"linear-gradient(135deg,#1e1b4b,#312e81)", padding:"16px 24px", flexShrink:0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12 }}>
          <Receipt size={20} color="#c4b5fd"/>
          <h2 style={{ fontSize:18, fontWeight:800, color:"#fff", margin:0 }}>Approvazione Note Spese</h2>
        </div>
        <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
          {[
            { id:"inviata",   label:`In attesa (${count.inviata})`,   bg:"#dbeafe", col:"#1d4ed8" },
            { id:"approvata", label:`Approvate (${count.approvata})`, bg:"#d1fae5", col:"#065f46" },
            { id:"respinta",  label:`Respinte (${count.respinta})`,   bg:"#fee2e2", col:"#991b1b" },
            { id:"tutti",     label:"Tutte",                           bg:"#f3f4f6", col:"#374151" },
          ].map(f => (
            <button key={f.id} onClick={() => setFiltroStato(f.id)}
              style={{ padding:"5px 12px", borderRadius:20, border:"none",
                background: filtroStato===f.id ? f.bg : "rgba(255,255,255,0.1)",
                color: filtroStato===f.id ? f.col : "rgba(255,255,255,0.6)",
                fontSize:11, fontWeight: filtroStato===f.id?700:400,
                cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Lista */}
      <div style={{ flex:1, overflowY:"auto", padding:24, background:"#f9fafb" }}>
        {loading && <div style={{ textAlign:"center", padding:40, color:"#9ca3af" }}>⏳ Caricamento...</div>}

        {!loading && noteFiltrate.length === 0 && (
          <div style={{ textAlign:"center", padding:60, color:"#9ca3af" }}>
            <Receipt size={40} color="#d1d5db" style={{ marginBottom:12 }}/>
            <div style={{ fontSize:14 }}>
              {filtroStato==="inviata" ? "Nessuna nota spese in attesa" : "Nessuna nota spese"}
            </div>
          </div>
        )}

        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {noteFiltrate.map(n => {
            const nome = nomeUtente(n.uid);
            const meseLabel = `${MESI[(n.mese||1)-1]} ${n.anno}`;
            const totale = totaleNota(n.righe);
            const statoInfo = STATI[n.stato] || STATI.inviata;

            return (
              <div key={n.id} style={{ background:"#fff", borderRadius:12, border:"1px solid #e5e7eb",
                padding:"14px 18px", display:"flex", alignItems:"center",
                justifyContent:"space-between", gap:12, flexWrap:"wrap" }}>

                <div style={{ display:"flex", alignItems:"center", gap:14 }}>
                  <div style={{ width:40, height:40, borderRadius:"50%", background:"#fef3c7",
                    display:"flex", alignItems:"center", justifyContent:"center",
                    fontSize:16, fontWeight:800, color:"#d97706", flexShrink:0 }}>
                    {nome[0]?.toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontSize:14, fontWeight:700, color:"#111827" }}>{nome}</div>
                    <div style={{ fontSize:12, color:"#6b7280" }}>{meseLabel}</div>
                    <div style={{ fontSize:11, color:"#9ca3af", marginTop:2 }}>
                      Totale: <strong style={{ color:"#d97706" }}>€ {totale.toFixed(2)}</strong>
                    </div>
                  </div>
                </div>

                <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                  <div>
                    <span style={{ fontSize:11, fontWeight:700, color:statoInfo.col,
                      background:statoInfo.bg, borderRadius:20, padding:"3px 10px" }}>
                      {statoInfo.label}
                    </span>
                    {(n.stato==="approvata"||n.stato==="respinta") && n[`${n.stato}Da`] && (
                      <div style={{ fontSize:10, color:"#9ca3af", marginTop:4 }}>
                        {n.stato==="approvata"?"✅ Approvata":"❌ Respinta"} da <strong style={{color:"#374151"}}>{nomeUtente(n[`${n.stato}Da`])}</strong>
                        {n[`${n.stato}At`]?.toDate && (
                          <span> il {n[`${n.stato}At`].toDate().toLocaleDateString("it-IT")}</span>
                        )}
                      </div>
                    )}
                  </div>

                  {n.stato === "inviata" && n.uid === currentUser?.uid && !isPlatformOwner && (
                    <div style={{fontSize:11.5,color:"#9ca3af",fontStyle:"italic",padding:"6px 4px"}}>
                      È una tua nota — serve un altro amministratore per approvarla
                    </div>
                  )}
                  {n.stato === "inviata" && (n.uid !== currentUser?.uid || isPlatformOwner) && (
                    <>
                      {n.uid === currentUser?.uid && isPlatformOwner && (
                        <span style={{fontSize:10,color:"#7c3aed",fontWeight:700,marginRight:4}} title="È una tua nota — puoi agire solo perché sei Platform Owner">⚠️ tua nota</span>
                      )}
                      <button onClick={() => aggiornaStato(n, "approvata")} disabled={elaborandoId===n.id}
                        style={{ display:"flex", alignItems:"center", gap:4, padding:"6px 14px",
                          border:"1px solid #059669", borderRadius:7, background:"#d1fae5",
                          color:"#059669", fontSize:12, fontWeight:700, cursor:elaborandoId===n.id?"default":"pointer", opacity:elaborandoId===n.id?0.6:1, fontFamily:"'Inter',sans-serif" }}>
                        <CheckSquare size={13}/> Approva
                      </button>
                      <button onClick={() => aggiornaStato(n, "respinta")} disabled={elaborandoId===n.id}
                        style={{ display:"flex", alignItems:"center", gap:4, padding:"6px 14px",
                          border:"1px solid #dc2626", borderRadius:7, background:"#fee2e2",
                          color:"#dc2626", fontSize:12, fontWeight:700, cursor:elaborandoId===n.id?"default":"pointer", opacity:elaborandoId===n.id?0.6:1, fontFamily:"'Inter',sans-serif" }}>
                        <X size={13}/> Respingi
                      </button>
                    </>
                  )}

                  {n.stato === "respinta" && (
                    <button onClick={() => aggiornaStato(n, "bozza")}
                      style={{ display:"flex", alignItems:"center", gap:4, padding:"6px 14px",
                        border:"1px solid #2563eb", borderRadius:7, background:"#dbeafe",
                        color:"#2563eb", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                      <RotateCcw size={13}/> Rimanda al compilatore
                    </button>
                  )}

                  {n.stato === "approvata" && canManage && (
                    <button onClick={() => aggiornaStato(n, "bozza")}
                      style={{ display:"flex", alignItems:"center", gap:4, padding:"6px 14px",
                        border:"1px solid #6b7280", borderRadius:7, background:"#f3f4f6",
                        color:"#374151", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                      <RotateCcw size={13}/> Riapri
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
